// DTOs/schemas do módulo "inbox" (caixa de respostas, respostas automáticas, horário de atendimento). Pertence ao pacote inbox.
import { z } from "zod";

import type { OrigemMensagem, StatusMensagem, TipoEnvio, TipoNumero } from "./enums";
import { zTipoNumero } from "./enums";
import type { ErroEnvio } from "./envio";

// ─── Conteúdo ────────────────────────────────────────────────────────────────

export const TIPOS_CONTEUDO = [
  "TEXTO",
  "IMAGEM",
  "AUDIO",
  "VIDEO",
  "DOCUMENTO",
  "LOCALIZACAO",
  "TEMPLATE",
  "CONTATO",
  "FIGURINHA",
  "ENQUETE",
  "OUTRO",
] as const;
export type TipoConteudoNome = (typeof TIPOS_CONTEUDO)[number];

export const TIPOS_REGRA_AUTO = ["PALAVRA_CHAVE", "BOAS_VINDAS", "AUSENCIA"] as const;
export type TipoRegraAutoNome = (typeof TIPOS_REGRA_AUTO)[number];

export const MODOS_MATCH = ["CONTEM", "IGUAL"] as const;
export type ModoMatchNome = (typeof MODOS_MATCH)[number];

/** Fuso fixo do horário de atendimento. */
export const FUSO_ATENDIMENTO = "America/Sao_Paulo" as const;

/** Limite de texto do WhatsApp. */
export const MAX_TEXTO_MENSAGEM = 4096;

// ─── Conversas (entrada) ─────────────────────────────────────────────────────

const textoOpcional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : undefined));

const booleanoDeQuery = z
  .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
  .optional()
  .transform((v) => v === true || v === "true" || v === "1");

/** `GET /conversas`. Ids são `publicId`. */
export const zConsultaConversas = z.object({
  numeroId: textoOpcional(64),
  tipo: zTipoNumero.optional(),
  busca: textoOpcional(100),
  naoLidas: booleanoDeQuery,
  cursor: textoOpcional(200),
  limite: z.coerce.number().int().min(1).max(50).default(30),
});
export type ConsultaConversas = z.output<typeof zConsultaConversas>;

/** `GET /conversas/:id` (mensagens do mais novo para o mais antigo; `cursor` traz as anteriores). */
export const zConsultaMensagensConversa = z.object({
  cursor: textoOpcional(200),
  limite: z.coerce.number().int().min(1).max(100).default(40),
});
export type ConsultaMensagensConversa = z.output<typeof zConsultaMensagensConversa>;

const textoMensagem = z
  .string()
  .trim()
  .min(1, "Escreva a mensagem.")
  .max(MAX_TEXTO_MENSAGEM, `Máximo de ${MAX_TEXTO_MENSAGEM} caracteres.`);

const variaveisTemplate = z.array(z.string().max(1024)).max(30).default([]);

const exigeTextoOuTemplate = (v: { texto?: string; templateId?: string }) =>
  (v.texto !== undefined) !== (v.templateId !== undefined);
const MSG_TEXTO_OU_TEMPLATE = "Informe o texto OU o template (um dos dois).";

/**
 * `POST /conversas/:id/mensagens`. Texto livre ou (só número oficial) template.
 * No oficial o ZapHub não valida janela nem template: a Meta responde e o erro volta ao cliente.
 */
export const zEnviarMensagemConversa = z
  .object({
    texto: textoMensagem.optional(),
    /** `publicId` do template (número oficial). */
    templateId: z.string().trim().min(1).max(64).optional(),
    variaveis: variaveisTemplate,
  })
  .refine(exigeTextoOuTemplate, { message: MSG_TEXTO_OU_TEMPLATE, path: ["texto"] });
export type EnviarMensagemConversa = z.output<typeof zEnviarMensagemConversa>;

/** `POST /conversas` — nova conversa por telefone (normalizado para E.164; DDI 55 se vier sem). */
export const zNovaConversa = z
  .object({
    numeroId: z.string({ required_error: "Escolha o número." }).trim().min(1, "Escolha o número.").max(64),
    telefone: z
      .string({ required_error: "Informe o telefone." })
      .trim()
      .min(8, "Telefone inválido.")
      .max(30, "Telefone inválido."),
    texto: textoMensagem.optional(),
    templateId: z.string().trim().min(1).max(64).optional(),
    variaveis: variaveisTemplate,
  })
  .refine(exigeTextoOuTemplate, { message: MSG_TEXTO_OU_TEMPLATE, path: ["texto"] });
export type NovaConversa = z.output<typeof zNovaConversa>;

// ─── Conversas (saída) ───────────────────────────────────────────────────────

export interface MidiaMensagem {
  tipo: "IMAGEM" | "AUDIO" | "VIDEO" | "DOCUMENTO";
  mimeType: string | null;
  /** Há conteúdo a baixar (false depois da retenção). */
  disponivel: boolean;
  /** Caminho relativo (autenticado) para baixar: `GET /conversas/:id/mensagens/:mid/midia`. */
  url: string | null;
}

export interface LocalizacaoMensagem {
  latitude: number;
  longitude: number;
}

/** Contato compartilhado (vCard) numa mensagem. */
export interface ContatoMensagem {
  nome: string;
  telefones: string[];
  empresa?: string;
  email?: string;
}

/** Enquete enviada pelo número, com o total atual de cada opção. */
export interface EnqueteMensagem {
  maxRespostas: number;
  opcoes: Array<{ opcao: string; total: number }>;
}

/** Mensagem citada (`respondendoA` no envio). */
export interface CitacaoMensagem {
  id: string;
  /** Começo do texto, ou nulo (mídia, apagada, retenção). */
  trecho: string | null;
}

export interface MensagemConversa {
  id: string;
  direcao: "ENTRADA" | "SAIDA";
  origem: OrigemMensagem;
  tipoEnvio: TipoEnvio | null;
  tipoConteudo: TipoConteudoNome;
  /** Nulo depois da retenção (`removida`). */
  texto: string | null;
  midia: MidiaMensagem | null;
  localizacao: LocalizacaoMensagem | null;
  status: StatusMensagem;
  /** Falha de envio: código e mensagem (em PT-BR, inclusive a da Meta). */
  erro: { codigo: string | null; mensagem: string | null } | null;
  enfileirada: boolean;
  contatoOptout: boolean;
  /** Só números não oficiais. */
  economia: { marketingCentavos: number; utilityCentavos: number } | null;
  criadoEm: string;
  enviadoEm: string | null;
  entregueEm: string | null;
  lidoEm: string | null;
  /** Conteúdo removido pela política de retenção; os metadados permanecem. */
  removida: boolean;
  /** ISO. O autor editou a mensagem (o texto já é o novo). */
  editadaEm: string | null;
  /** ISO. O autor apagou para todos (texto e mídia foram limpos). */
  apagadaEm: string | null;
  /** Reação atual de cada lado da conversa. */
  reacoes: ReacaoMensagemDto[];
  contatos: ContatoMensagem[] | null;
  enquete: EnqueteMensagem | null;
  respondendoA: CitacaoMensagem | null;
  /** Mensagem de grupo: quem escreveu (nulo nas enviadas pelo número e em conversa individual). */
  remetente: { telefone: string | null; nome: string | null } | null;
  /** Interativa enviada (oficial): tipo e títulos das opções. */
  interativo: { tipo: "botoes" | "lista" | "link"; opcoes: string[] } | null;
  /** O contato tocou num botão ou item. */
  interacao: { tipo: "botao" | "lista"; id: string; titulo: string } | null;
}

export interface ReacaoMensagemDto {
  autor: "contato" | "numero";
  emoji: string;
}

export interface NumeroDaConversa {
  id: string;
  apelido: string;
  tipo: TipoNumero;
  telefone: string | null;
  status: string;
}

export interface LeadDaConversa {
  id: string;
  status: string;
  origem: string;
}

export interface ConversaResumo {
  id: string;
  numero: NumeroDaConversa;
  contato: { telefone: string; nome: string | null };
  ultimaMensagemEm: string;
  ultimaEntradaEm: string | null;
  /** Janela de 24h (informativa): aberta se o contato escreveu nas últimas 24h. */
  janela24h: { aberta: boolean; expiraEm: string | null };
  naoLidas: number;
  /** Conversa de grupo (F5): `contato.telefone` traz o JID do grupo e `contato.nome` o nome dele. */
  grupo: boolean;
  /** Etiquetas do WhatsApp Business aplicadas (F6). */
  etiquetas: Array<{ id: string; nome: string; cor: number }>;
  /** Estado de organização no WhatsApp do número (F4). */
  arquivada: boolean;
  fixada: boolean;
  silenciadaAte: string | null;
  ultimaMensagem: {
    trecho: string | null;
    direcao: "ENTRADA" | "SAIDA";
    tipoConteudo: TipoConteudoNome;
    origem: OrigemMensagem;
  } | null;
  lead: LeadDaConversa | null;
}

export interface ListaConversas {
  itens: ConversaResumo[];
  proximoCursor: string | null;
  /** Total de não lidas de TODAS as conversas da conta (selo do menu). */
  totalNaoLidas: number;
}

export interface DetalheConversa {
  conversa: ConversaResumo;
  mensagens: { itens: MensagemConversa[]; proximoCursor: string | null };
}

/** Resposta de enviar/iniciar conversa: o resultado do `EnvioService` + a mensagem gravada (inclusive FALHOU com o erro). */
export interface RespostaEnvioConversa {
  conversaId: string;
  mensagemId: string;
  status: StatusMensagem;
  tipoEnvio: TipoEnvio;
  enfileirada: boolean;
  custoEvitado: { marketingCentavos: number; utilityCentavos: number } | null;
  contatoOptout: boolean;
  janelaAberta: boolean;
  /** Só quando `status = FALHOU`: o erro do canal (na Meta, com o código dela). */
  erro: ErroEnvio | null;
  mensagem: MensagemConversa | null;
}

// ─── Respostas automáticas ───────────────────────────────────────────────────

const gatilho = z.string().trim().min(1, "Informe a palavra-chave.").max(200);
const respostaAuto = z
  .string()
  .trim()
  .min(1, "Escreva a resposta.")
  .max(MAX_TEXTO_MENSAGEM, `Máximo de ${MAX_TEXTO_MENSAGEM} caracteres.`);

/** `POST /numeros/:id/auto-respostas`. Palavra-chave exige `gatilho`; boas-vindas e ausência são únicas por número. */
export const zCriarRegraAutoResposta = z
  .object({
    tipo: z.enum(TIPOS_REGRA_AUTO),
    modo: z.enum(MODOS_MATCH).default("CONTEM"),
    gatilho: gatilho.optional(),
    resposta: respostaAuto,
    ativo: z.boolean().default(true),
    ordem: z.number().int().min(0).max(1000).default(0),
  })
  .superRefine((v, ctx) => {
    if (v.tipo === "PALAVRA_CHAVE" && !v.gatilho) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["gatilho"], message: "Informe a palavra-chave." });
    }
  })
  .transform((v) => (v.tipo === "PALAVRA_CHAVE" ? v : { ...v, gatilho: undefined, modo: "CONTEM" as const }));
export type CriarRegraAutoResposta = z.output<typeof zCriarRegraAutoResposta>;

/** `PATCH /numeros/:id/auto-respostas/:regraId` — o tipo não muda. */
export const zAtualizarRegraAutoResposta = z
  .object({
    modo: z.enum(MODOS_MATCH).optional(),
    gatilho: gatilho.optional(),
    resposta: respostaAuto.optional(),
    ativo: z.boolean().optional(),
    ordem: z.number().int().min(0).max(1000).optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "Informe ao menos um campo." });
export type AtualizarRegraAutoResposta = z.output<typeof zAtualizarRegraAutoResposta>;

export interface RegraAutoRespostaDto {
  id: string;
  tipo: TipoRegraAutoNome;
  modo: ModoMatchNome;
  gatilho: string | null;
  resposta: string;
  ativo: boolean;
  ordem: number;
}

export interface ListaRegrasAutoResposta {
  itens: RegraAutoRespostaDto[];
}

// ─── Horário de atendimento ──────────────────────────────────────────────────

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use o formato HH:mm.");

const diaAtendimento = z
  .object({
    /** 0 = domingo … 6 = sábado. */
    diaSemana: z.number().int().min(0).max(6),
    inicio: hhmm,
    fim: hhmm,
    ativo: z.boolean().default(true),
  })
  .refine((d) => d.inicio < d.fim, { message: "O início deve ser antes do fim.", path: ["fim"] });

/**
 * `PUT /numeros/:id/horario-atendimento` — substitui o horário inteiro. Dia ausente (ou inativo) =
 * fechado. Sem nenhum dia ativo = sem horário definido (a ausência nunca dispara).
 */
export const zHorarioAtendimento = z
  .object({ dias: z.array(diaAtendimento).max(7) })
  .refine((v) => new Set(v.dias.map((d) => d.diaSemana)).size === v.dias.length, {
    message: "Cada dia da semana só pode aparecer uma vez.",
    path: ["dias"],
  });
export type HorarioAtendimentoInput = z.output<typeof zHorarioAtendimento>;

export interface HorarioAtendimentoDto {
  fuso: typeof FUSO_ATENDIMENTO;
  dias: Array<{ diaSemana: number; inicio: string; fim: string; ativo: boolean }>;
}
